<template>
  <!-- ====== 阶段1：加载页面（全屏） ====== -->
  <div v-if="phase === 'loading'" class="loading-overlay" :class="{ 'is-mobile': isMobile }">
    <img :src="loadingGifSrc" class="loading-gif" alt="loading" />
    <img v-show="showClickBtn" class="click-enter-btn" :src="clickEnterSrc" alt="点击进入" @click="goToVideo" />
  </div>

  <!-- ====== 阶段2：开始页面（视频 + 进入专题） ====== -->
  <div v-else-if="phase === 'start'" class="start-overlay" :class="{ 'is-mobile': isMobile }">
    <video
      ref="videoRef"
      :src="videoSrc"
      controls
      playsinline
      webkit-playsinline
      class="start-video"
      :poster="posterSrc"
      @ended="onVideoEnded"
    ></video>
    <img class="enter-btn" :src="enterBtnSrc" alt="进入专题" @click="enterMain" />
  </div>

  <!-- ====== 阶段3：主内容 ====== -->
  <component v-else :is="currentComponent" />
</template>

<script>
import { ref, nextTick, onMounted, onBeforeUnmount, defineAsyncComponent, defineComponent, provide } from "vue";
import backgroundConfig from "./config/background.js";
import foregroundConfig from "./config/foreground.js";
import middlegroundConfig from "./config/middleground.js";
import busConfig from "./config/bus.js";
import { triggerButtonSrc } from "./config/triggers.js";
import { endingSrc } from "./config/ending.js";
import { titleImgSrc as introTitleSrc } from "./config/intro.js";

export default defineComponent({
  setup() {
    var phase = ref("loading"); // loading → start → main
    var isMobile = ref(false);

    var loadingGifSrc = "./xiangan/gif/\u52A0\u8F7D\u9875\u9762.gif";
    var videoSrc = "./xiangan/fengmianmp4.mp4";
    var posterSrc = "./xiangan/fengmiantupian.png";
    var enterBtnSrc = "./xiangan/jinruzhuanti.png";
    var clickEnterSrc = "./xiangan/dianjijinru.png";
    var showClickBtn = ref(false);

    var videoRef = ref(null);
    var currentComponent = ref(null);

    // ---------- 设备检测 ----------
    var checkMobile = function() {
      var ua = navigator.userAgent.toLowerCase();
      var mobileRegex = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/;
      var tabletRegex = /ipad|tablet/;
      if (tabletRegex.test(ua)) return false;
      if (mobileRegex.test(ua)) return true;
      // 小屏幕也当手机处理
      return window.innerWidth <= 768 && !/ipad|tablet/i.test(navigator.userAgent);
    };

    var setComponent = function() {
      isMobile.value = checkMobile();
      if (isMobile.value) {
        currentComponent.value = defineAsyncComponent(function() {
          return import("./views/XionganMobile.vue");
        });
      } else {
        currentComponent.value = defineAsyncComponent(function() {
          return import("./views/XionganPC.vue");
        });
      }
    };

    // ---------- 点击进入视频页 ----------
    var goToVideo = function() {
      phase.value = "start";
      nextTick(function() {
        if (videoRef.value) {
          videoRef.value.play().catch(function() {});
        }
      });
    };

    // ---------- 视频结束 ----------
    var onVideoEnded = function() {
      // 视频播完也可以自动进入
    };

    // ---------- 进入专题 ----------
    var userInteracted = ref(false);
    provide("userInteracted", userInteracted);

    // ==================== 图片顺序预加载（loading 阶段即启动） ====================
    var _preloadedCount = ref(0);
    var _preloadedMap = {};

    var getSrc = function(realSrc) {
      _preloadedCount.value;
      return _preloadedMap[realSrc];
    };
    provide("getSrc", getSrc);

    var preloadAllImages = function() {
      var queue = [];
      queue.push(introTitleSrc);
      for (var i = 0; i < backgroundConfig.length; i++) queue.push(backgroundConfig[i].src);
      var mgSorted = middlegroundConfig.slice().sort(function(a, b) { return (a.left || 0) - (b.left || 0); });
      for (var i = 0; i < mgSorted.length; i++) queue.push(mgSorted[i].src);
      for (var i = 0; i < foregroundConfig.length; i++) queue.push(foregroundConfig[i].src);
      queue.push(endingSrc);
      queue.push(busConfig.src);
      queue.push(triggerButtonSrc);
      var seen = {};
      var unique = [];
      for (var i = 0; i < queue.length; i++) {
        if (queue[i] && !seen[queue[i]]) { seen[queue[i]] = true; unique.push(queue[i]); }
      }
      var loadNext = function(idx) {
        if (idx >= unique.length) return;
        var img = new Image();
        img.onload = img.onerror = function() {
          _preloadedMap[unique[idx]] = unique[idx];
          _preloadedCount.value++;
          loadNext(idx + 1);
        };
        img.src = unique[idx];
      };
      loadNext(0);
    };

    var enterMain = function() {
      userInteracted.value = true;
      setComponent();
      phase.value = "main";
    };

    // ---------- 生命周期 ----------
    onMounted(function() {
      preloadAllImages();
      if (document.fonts) {
        document.fonts.load('300 1em "FZZhengDaHei"').catch(function() {});
      }
      isMobile.value = checkMobile();

      // 加载动画播放 3 秒后显示"点击进入"按钮
      setTimeout(function() {
        showClickBtn.value = true;
      }, 3000);

      window.addEventListener("resize", function() {
        var wasMobile = isMobile.value;
        isMobile.value = checkMobile();
        if (phase.value === "main" && isMobile.value !== wasMobile) {
          setComponent();
        }
      });
    });

    return {
      phase,
      isMobile,
      loadingGifSrc,
      videoSrc,
      posterSrc,
      enterBtnSrc,
      clickEnterSrc,
      showClickBtn,
      videoRef,
      currentComponent,
      goToVideo,
      onVideoEnded,
      enterMain,
    };
  },
});
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

img {
  user-select: none;
  -webkit-user-drag: none;
}

/* ====== 加载页面 - PC 全屏显示 ====== */
.loading-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: #000;
  z-index: 9999;
  overflow: hidden;
}
.loading-gif {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 点击进入按钮 - 加载页底部居中 */
.click-enter-btn {
  position: absolute;
  bottom: 18%;
  left: 50%;
  transform: translateX(-50%);
  height: 7vh;
  width: auto;
  cursor: pointer;
  z-index: 10;
  object-fit: contain;
  animation: click-btn-breathe 2s ease-in-out infinite;
}
@keyframes click-btn-breathe {
  0%, 100% { transform: translateX(-50%) scale(1); }
  50% { transform: translateX(-50%) scale(1.08); }
}
.click-enter-btn:active {
  animation: none;
  transform: translateX(-50%) scale(0.95);
}

/* ====== 加载页面 - 手机端旋转横屏 ====== */
.loading-overlay.is-mobile {
  position: fixed;
  top: 0; left: 0;
  width: 100vh;
  height: 100vw;
  transform: rotate(90deg) translateY(-100%);
  transform-origin: top left;
}
.loading-overlay.is-mobile .click-enter-btn {
  height: 10vw;
  bottom: 15%;
}

/* ====== 开始页面 - PC ====== */
.start-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: #000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.start-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

/* ====== 开始页面 - 手机端旋转横屏 ====== */
.start-overlay.is-mobile {
  width: 100vh !important;
  height: 100vw !important;
  transform: rotate(90deg) translateY(-100%);
  transform-origin: top left;
}
.start-overlay.is-mobile .start-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 进入专题按钮 - 右上角（图片按钮） */
.enter-btn {
  position: absolute;
  top: 2.5vh;
  right: 2.5vh;
  height: 7vh;
  width: auto;
  cursor: pointer;
  transition: transform 0.2s ease;
  z-index: 10;
  object-fit: contain;
  border: none;
  background: none;
}
.enter-btn:hover {
  transform: scale(1.06);
}
.enter-btn:active {
  transform: scale(0.97);
}
.is-mobile .enter-btn {
  position: fixed;
  top: 3vw;
  right: 3vw;
  height: 10vw;
}
</style>
